import { Autocomplete, FormControl, TextField } from '@mui/material';
import { FC, useEffect, useMemo } from 'react';
import { useGetAllOrganizations } from '../api-http/organizations';
import { useAuth } from '../context/auth-context';
import { UserRole } from '../types/user.types';
import { useIsUserRole } from '../util/useIsUserRole';
import {
  getOrganizationById,
  getSwitchableOrganizations,
} from './organizationSwitcherUtils';

const OrganizationSwitch: FC = () => {
  const isSuperAdmin = useIsUserRole([UserRole.superAdmin]);
  const { setSwitchedOrgId, currentUser, switchedOrgId } = useAuth();
  const { data: organizations } = useGetAllOrganizations();
  const currentOrganizationId = currentUser?.organization?._id;
  const filteredOrganizations = useMemo(
    () =>
      organizations && currentOrganizationId
        ? getSwitchableOrganizations(organizations, currentOrganizationId)
        : [],
    [currentOrganizationId, organizations],
  );

  useEffect(() => {
    if (
      organizations &&
      currentOrganizationId &&
      switchedOrgId &&
      !filteredOrganizations.some(
        (organization) => organization._id === switchedOrgId,
      )
    ) {
      setSwitchedOrgId(null);
    }
  }, [
    currentOrganizationId,
    filteredOrganizations,
    organizations,
    setSwitchedOrgId,
    switchedOrgId,
  ]);

  if (!isSuperAdmin || !filteredOrganizations?.length) {
    return null;
  }
  const defaultValue = getOrganizationById(
    filteredOrganizations,
    switchedOrgId,
  );
  return (
    <FormControl sx={{ minWidth: { xs: 150, sm: 210 } }}>
      <Autocomplete
        disablePortal
        id="organization-selector"
        options={filteredOrganizations.map(({ name: label, _id: value }) => ({
          label,
          value,
        }))}
        value={defaultValue}
        sx={{
          width: { xs: 150, sm: 210 },
          '& .MuiOutlinedInput-root': {
            minHeight: 36,
            py: 0,
            bgcolor: '#f8fafc',
            borderRadius: 2,
          },
          '& .MuiOutlinedInput-input': { py: 1, fontSize: 13 },
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            size="small"
            placeholder="Select organization"
            inputProps={{
              ...params.inputProps,
              'aria-label': 'Select organization',
            }}
          />
        )}
        onChange={(_e, option) => {
          if (option) {
            setSwitchedOrgId(option.value);
          }
        }}
        clearIcon={false}
        isOptionEqualToValue={(option, value) => option.value === value.value}
      />
    </FormControl>
  );
};

export default OrganizationSwitch;
